import { useState } from 'react';
import { Tags as TagsIcon } from 'lucide-react';

import type { RecursoTag, TagResumo } from '@/api/tags';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { TagChips } from '@/components/ui/tag-chip';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAplicarTags } from '@/hooks/useTags';
import type { CacheDeTags } from '@/hooks/useTagsNoCache';
import { usePodeColaborar } from '@/lib/delegacao/atuacao';
import { cn } from '@/lib/utils';

import { TagsDialog } from './TagsDialog';
import { TagsRapidas } from './TagsRapidas';

interface TagsDoRegistroProps {
  recurso: RecursoTag;
  registroId: string;
  /** Título do diálogo completo ("Tags — <identificador>"). */
  identificador: string;
  tags: TagResumo[] | undefined;
  /** Os caches da tela (otimista; a resposta reconcilia) — `useTagsNoCache`. */
  noCache: CacheDeTags;
  /** Quantos chips antes do "+N". */
  max?: number;
  /**
   * Chips só a partir do `sm` (o botão fica): cabeçalho dos autos e cards de celular,
   * onde a linha não tem largura para eles.
   */
  compacto?: boolean;
  /**
   * Avisa quando o popup ou o diálogo abre e fecha. Tela que filtra no cliente mantém o
   * registro visível enquanto ele está aberto: desmarcar a tag do filtro não pode
   * desmontar o popup no meio da edição.
   */
  onAbertoChange?: (aberto: boolean) => void;
  className?: string;
}

/**
 * Chips do registro e o ícone de etiqueta, num GATILHO SÓ: clicar numa etiqueta ou no
 * ícone abre o popup rápido (um clique marca ou desmarca; atalho para criar) e, dele,
 * "Gerenciar tags…" abre o diálogo completo (categorias, tag do escritório, editar,
 * excluir). Antes só o ícone abria, e a etiqueta — o alvo maior e o que o olho procura
 * — não fazia nada (Adriano, 26/10). Tudo aplica na hora, offline inclusive (outbox).
 * Delegação de Leitura vê os chips, sem clique.
 *
 * Popup e diálogo só montam abertos: a lista de mil registros carrega mil botões, não
 * mil formulários.
 */
export function TagsDoRegistro({
  recurso,
  registroId,
  identificador,
  tags,
  noCache,
  max,
  compacto = false,
  onAbertoChange,
  className,
}: TagsDoRegistroProps) {
  const [popup, setPopup] = useState(false);
  const [dialogo, setDialogo] = useState(false);
  const podeColaborar = usePodeColaborar();
  const atuais = tags ?? [];
  // O nome do gatilho diz as etiquetas que ele mostra e o que o clique faz.
  const rotuloDoGatilho = atuais.length
    ? `Editar tags: ${atuais.map((t) => t.nome).join(', ')}`
    : 'Adicionar tags';

  const mudarPopup = (valor: boolean) => {
    setPopup(valor);
    if (!dialogo) onAbertoChange?.(valor);
  };
  const mudarDialogo = (valor: boolean) => {
    setDialogo(valor);
    onAbertoChange?.(valor);
  };

  return (
    <span
      className={cn('inline-flex min-w-0 items-center gap-1', className)}
      // O popup e o diálogo vivem num portal, mas o clique sobe pela árvore do React:
      // sem isto, cada toque neles "clicaria" também na linha da lista por trás.
      onClick={(e) => e.stopPropagation()}
    >
      {!podeColaborar && (
        <TagChips tags={atuais} max={max} className={compacto ? 'hidden sm:inline-flex' : undefined} />
      )}
      {podeColaborar && (
        <Popover open={popup} onOpenChange={mudarPopup}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  // Altura MÍNIMA, não fixa: as etiquetas quebram linha em célula
                  // estreita, e numa caixa de 32px elas vazavam sobre a linha de baixo.
                  className="h-auto min-h-11 min-w-11 shrink justify-start gap-1 whitespace-normal px-1.5 py-1 text-left text-muted-foreground sm:min-h-8 sm:min-w-8"
                  aria-label={rotuloDoGatilho}
                >
                  <TagChips
                    tags={atuais}
                    max={max}
                    className={compacto ? 'hidden sm:inline-flex' : undefined}
                  />
                  <TagsIcon className="h-4 w-4 shrink-0" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>{atuais.length ? 'Editar tags' : 'Adicionar tags'}</TooltipContent>
          </Tooltip>
          <PopoverContent align="end" className="w-72 p-0" onClick={(e) => e.stopPropagation()}>
            <TagsRapidas
              recurso={recurso}
              registroId={registroId}
              aplicadas={atuais}
              noCache={noCache}
              onGerenciar={() => {
                setPopup(false);
                mudarDialogo(true);
              }}
              onCriada={() => mudarPopup(false)}
            />
          </PopoverContent>
        </Popover>
      )}
      {dialogo && (
        <DialogoDoRegistro
          recurso={recurso}
          registroId={registroId}
          identificador={identificador}
          aplicadas={atuais}
          noCache={noCache}
          onOpenChange={mudarDialogo}
        />
      )}
    </span>
  );
}

function DialogoDoRegistro({
  recurso,
  registroId,
  identificador,
  aplicadas,
  noCache,
  onOpenChange,
}: {
  recurso: RecursoTag;
  registroId: string;
  identificador: string;
  aplicadas: TagResumo[];
  noCache: CacheDeTags;
  onOpenChange: (aberto: boolean) => void;
}) {
  const { definir } = useAplicarTags(recurso, noCache);
  return (
    <TagsDialog
      open
      onOpenChange={onOpenChange}
      identificador={identificador}
      aplicadas={aplicadas}
      onChange={(novas) => void definir(registroId, novas, aplicadas)}
    />
  );
}
